/**
 * Logger Utility
 * Development-only logger that is automatically removed in production builds
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  data?: any;
  error?: Error | string;
}

class Logger {
  private isDevelopment: boolean;
  private prefix: string;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.prefix = '[BCP]';
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    let formattedMessage = `${this.prefix} [${timestamp}] [${level.toUpperCase()}]`;
    
    if (context?.component) {
      formattedMessage += ` [${context.component}]`;
    }
    
    if (context?.action) {
      formattedMessage += ` ${context.action}:`;
    }
    
    formattedMessage += ` ${message}`;
    
    return formattedMessage;
  }

  private log(level: LogLevel, message: string, context?: LogContext): void {
    if (!this.isDevelopment) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, context);
    const consoleMethod = console[level] || console.log;
    
    if (context?.data) {
      consoleMethod(formattedMessage, context.data);
    } else if (context?.error) {
      consoleMethod(formattedMessage, context.error);
    } else {
      consoleMethod(formattedMessage);
    }
  }

  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  error(message: string, context?: LogContext): void {
    this.log('error', message, context);
  }

  // Specialized methods for common use cases
  hookStart(hookName: string, params?: any): void {
    this.debug(`Hook started`, {
      component: hookName,
      action: 'start',
      data: params
    });
  }

  hookEnd(hookName: string, result?: any): void {
    this.debug(`Hook completed`, {
      component: hookName,
      action: 'end',
      data: result
    });
  }

  apiRequest(url: string, method: string, data?: any): void {
    this.info(`API request`, {
      component: 'API',
      action: `${method} ${url}`,
      data
    });
  }

  apiResponse(url: string, method: string, response?: any): void {
    this.info(`API response`, {
      component: 'API',
      action: `${method} ${url}`,
      data: response
    });
  }

  apiError(url: string, method: string, error: Error | string): void {
    this.error(`API error`, {
      component: 'API',
      action: `${method} ${url}`,
      error
    });
  }

  formValidation(formName: string, errors: any[]): void {
    if (errors.length > 0) {
      this.warn(`Form validation failed`, {
        component: formName,
        action: 'validation',
        data: errors
      });
    } else {
      this.debug(`Form validation passed`, {
        component: formName,
        action: 'validation'
      });
    }
  }

  stateChange(component: string, from: any, to: any): void {
    this.debug(`State changed`, {
      component,
      action: 'state-change',
      data: { from, to }
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export individual methods for convenience
export const {
  debug,
  info,
  warn,
  error,
  hookStart,
  hookEnd,
  apiRequest,
  apiResponse,
  apiError,
  formValidation,
  stateChange
} = logger;